// External library imports
import { Collapsible, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Link, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

// Internal component imports
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";

// Data and utilities
import { NAVIGATION_ITEMS } from "../data/navigation";
import { cn } from "@/lib/utils";
import { hasPermission } from "@/lib/permissions";
import { useAuth } from "@/providers/auth.provider";

// Constants for styling
const MENU_BUTTON_STYLES = {
  base: "h-10 w-full transition-colors",
  active:
    "data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground",
  activeHover: "data-[active=true]:hover:bg-sidebar-accent",
};

const LINK_STYLES = {
  base: "flex h-10 w-full items-center justify-center gap-2 rounded-md font-medium",
  expanded: "px-2",
};

/**
 * Main navigation component for the application dashboard
 * Renders a collapsible sidebar with navigation items
 */
export function AppNavMain() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { open, toggleSidebar, isMobile } = useSidebar();
  const { t } = useTranslation();
  const { user } = useAuth();

  // Filter navigation items based on user permissions
  const visibleItems = NAVIGATION_ITEMS.filter((item) => {
    if (!item.requiredPermission) return true;
    if (item.hideOnMobile && isMobile) return false;
    return hasPermission(user, item.requiredPermission);
  });

  function isRouteActive(item: { url: string; matchUrls?: string[] }, pathname: string) {
    // Exact match for the item URL
    if (item.url === pathname) return true;
    
    // Check matchUrls - only match if it's not a sub-route
    if (item.matchUrls?.length) {
      return item.matchUrls.some((matchUrl: string) => {
        // For dashboard root, only match exact path
        if (matchUrl === "/dashboard") {
          return pathname === "/dashboard";
        }
        // For other routes, match if it starts with the URL
        return pathname.startsWith(matchUrl);
      });
    }
    
    return false;
  }

  return (
    <SidebarGroup>
      <SidebarGroupLabel>{t('nav.navigation')}</SidebarGroupLabel>
      <SidebarMenu>
        {visibleItems.map((item) => {
          const isActive = isRouteActive(item, pathname);

          return (
            <Collapsible
              key={item.titleKey}
              asChild
              defaultOpen={!!isActive}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    tooltip={t(item.titleKey)}
                    className={cn(
                      MENU_BUTTON_STYLES.base,
                      MENU_BUTTON_STYLES.active,
                      MENU_BUTTON_STYLES.activeHover,
                    )}
                    data-active={isActive}
                    onClick={() => (isMobile ? toggleSidebar() : null)}
                  >
                    <Link
                      to={item.url}
                      className={cn(
                        LINK_STYLES.base,
                        open && LINK_STYLES.expanded,
                      )}
                    >
                      {item.icon && (
                        <item.icon className="h-5 w-5 flex-shrink-0" />
                      )}
                      {open && (
                        <span className="sidebar-expanded:flex sidebar-collapsed:hidden flex-1 truncate">
                          {t(item.titleKey)}
                        </span>
                      )}
                    </Link>
                  </SidebarMenuButton>
                </CollapsibleTrigger>
              </SidebarMenuItem>
            </Collapsible>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}
